package org.example.project

import kotlin.js.Promise
import kotlinx.coroutines.await

external fun encodeURIComponent(uri: String): String

@JsModule("firebase/app")
@JsNonModule
external object FirebaseAppPkg {
    fun initializeApp(config: dynamic): dynamic
}

@JsModule("firebase/auth")
@JsNonModule
external object FirebaseAuthPkg {
    fun getAuth(app: dynamic = definedExternally): dynamic
    fun signInWithPopup(auth: dynamic, provider: dynamic): Promise<dynamic>
    fun signInWithEmailAndPassword(auth: dynamic, email: String, pass: String): Promise<dynamic>
    fun sendEmailVerification(user: dynamic): Promise<Unit>
    fun signOut(auth: dynamic): Promise<Unit>
    fun onAuthStateChanged(auth: dynamic, observer: (dynamic) -> Unit): () -> Unit
    
    class GoogleAuthProvider
    class OAuthProvider(providerId: String)
}

@JsModule("firebase/firestore")
@JsNonModule
external object FirebaseFirestorePkg {
    fun getFirestore(app: dynamic = definedExternally): dynamic
    fun doc(firestore: dynamic, path: String, vararg pathSegments: String): dynamic
    fun onSnapshot(reference: dynamic, observer: (dynamic) -> Unit): () -> Unit
}

class JsPlatform: Platform {
    override val name: String = "Web with Kotlin/JS"
}

actual fun getPlatform(): Platform = JsPlatform()

private var authInstance: dynamic = null
private var firestoreInstance: dynamic = null

actual fun initializeFirebase(config: FirebaseConfig) {
    val jsConfig = js("{}")
    jsConfig.apiKey = config.apiKey
    jsConfig.authDomain = config.authDomain
    jsConfig.projectId = config.projectId
    jsConfig.storageBucket = config.storageBucket
    jsConfig.messagingSenderId = config.messagingSenderId
    jsConfig.appId = config.appId
    jsConfig.measurementId = config.measurementId

    val app = FirebaseAppPkg.initializeApp(jsConfig)
    authInstance = FirebaseAuthPkg.getAuth(app)
    firestoreInstance = FirebaseFirestorePkg.getFirestore(app)
}

actual suspend fun signInWithGoogle(): Boolean {
    return try {
        val provider = FirebaseAuthPkg.GoogleAuthProvider()
        val promise = FirebaseAuthPkg.signInWithPopup(authInstance, provider)
        val result = promise.await()
        val user = result.user
        println("Google sign in success: ${user.email}")
        true
    } catch (e: Exception) {
        println("Google sign in failed: ${e.message}")
        false
    }
}

actual suspend fun signInWithApple(): Boolean {
    return try {
        val provider = FirebaseAuthPkg.OAuthProvider("apple.com")
        val promise = FirebaseAuthPkg.signInWithPopup(authInstance, provider)
        val result = promise.await()
        val user = result.user
        println("Apple sign in success: ${user.email}")
        true
    } catch (e: Exception) {
        println("Apple sign in failed: ${e.message}")
        false
    }
}

actual suspend fun signInWithEmail(email: String, pass: String): String? {
    return try {
        val result = FirebaseAuthPkg.signInWithEmailAndPassword(authInstance, email, pass).await()
        val user = result.user
        if (user.emailVerified == true) {
            println("Email sign in success: ${user.email}")
            null // Success
        } else {
            println("Email not verified: ${user.email}")
            FirebaseAuthPkg.signOut(authInstance).await()
            "Email not verified. Please check your inbox."
        }
    } catch (e: Throwable) {
        println("Email sign in failed: ${e.message}")
        e.message ?: "Unknown login error"
    }
}

actual suspend fun signOut() {
    try {
        FirebaseAuthPkg.signOut(authInstance).await()
    } catch (e: Exception) {
        println("Error signing out: ${e.message}")
    }
}

actual fun listenToAuthState(onStateChanged: (Boolean) -> Unit) {
    if (authInstance != null) {
        FirebaseAuthPkg.onAuthStateChanged(authInstance) { user ->
            onStateChanged(user != null)
        }
    } else {
        onStateChanged(false)
    }
}

actual fun getCurrentUserDisplayName(): String? {
    return authInstance?.currentUser?.displayName as? String
}

actual fun getCurrentUserEmail(): String? {
    return authInstance?.currentUser?.email as? String
}

actual fun getCurrentUserUid(): String? {
    return authInstance?.currentUser?.uid as? String
}

actual suspend fun getCurrentUserIdToken(): String? {
    val user = authInstance?.currentUser
    return if (user != null) {
        try {
            val tokenPromise: Promise<String> = user.getIdToken(true)
            tokenPromise.await()
        } catch (e: Exception) {
            println("Error getting ID token: ${e.message}")
            null
        }
    } else {
        null
    }
}

actual fun listenToUserDocument(uid: String, onDocumentUpdate: (UserDocument?) -> Unit): () -> Unit {
    if (firestoreInstance == null) {
        println("Firestore not initialized")
        return {}
    }

    val docRef = FirebaseFirestorePkg.doc(firestoreInstance, "users", uid)
    val unsubscribe = FirebaseFirestorePkg.onSnapshot(docRef) { docSnap ->
        val exists = js("docSnap.exists()") as Boolean
        if (exists) {
            try {
                // We use dynamic properties and serialize to avoid reflection issues or map them properly
                val data = js("docSnap.data()")
                
                // Convert Firestore Timestamp to string because UserDocument expects a String for created_at
                val jsonStr = js("""
                    (function(obj) {
                        function convert(o) {
                            for (var key in o) {
                                if (o[key] && typeof o[key] === 'object') {
                                    if (typeof o[key].toDate === 'function') {
                                        o[key] = o[key].toDate().toISOString();
                                    } else {
                                        convert(o[key]);
                                    }
                                }
                            }
                        }
                        convert(obj);
                        return JSON.stringify(obj);
                    })
                """)(data) as String

                // Parse it back to UserDocument
                val json = kotlinx.serialization.json.Json { 
                    ignoreUnknownKeys = true
                    isLenient = true
                    coerceInputValues = true 
                }
                val doc = json.decodeFromString<UserDocument>(jsonStr)
                onDocumentUpdate(doc)
            } catch (e: Exception) {
                println("Failed to parse UserDocument snapshot: ${e.message}")
                println("Raw JSON was: " + js("JSON.stringify(docSnap.data())"))
                onDocumentUpdate(null)
            }
        } else {
            println("UserDocument does not exist for uid: $uid")
            onDocumentUpdate(null)
        }
    }

    return {
        // execute unsubscribe
        val jsUnsubscribe: dynamic = unsubscribe
        jsUnsubscribe()
    }
}

actual fun listenToSystemProfileDocument(uid: String, onDocumentUpdate: (SystemProfileDocument?) -> Unit): () -> Unit {
    if (firestoreInstance == null) {
        println("Firestore not initialized")
        return {}
    }

    val docRef = FirebaseFirestorePkg.doc(firestoreInstance, "system_profiles", uid)
    val unsubscribe = FirebaseFirestorePkg.onSnapshot(docRef) { docSnap ->
        val exists = js("docSnap.exists()") as Boolean
        if (exists) {
            try {
                val data = js("docSnap.data()")
                
                val jsonStr = js("""
                    (function(obj) {
                        function convert(o) {
                            for (var key in o) {
                                if (o[key] && typeof o[key] === 'object') {
                                    if (typeof o[key].toDate === 'function') {
                                        o[key] = o[key].toDate().toISOString();
                                    } else {
                                        convert(o[key]);
                                    }
                                }
                            }
                        }
                        convert(obj);
                        return JSON.stringify(obj);
                    })
                """)(data) as String

                val json = kotlinx.serialization.json.Json { 
                    ignoreUnknownKeys = true
                    isLenient = true
                    coerceInputValues = true 
                }
                val doc = json.decodeFromString<SystemProfileDocument>(jsonStr)
                onDocumentUpdate(doc)
            } catch (e: Exception) {
                println("Failed to parse SystemProfileDocument snapshot: ${e.message}")
                println("Raw JSON was: " + js("JSON.stringify(docSnap.data())"))
                onDocumentUpdate(null)
            }
        } else {
            println("SystemProfileDocument does not exist for uid: $uid")
            onDocumentUpdate(null)
        }
    }

    return {
        val jsUnsubscribe: dynamic = unsubscribe
        jsUnsubscribe()
    }
}

actual fun getUserAgent(): String {
    return kotlinx.browser.window.navigator.userAgent
}
